/**
 * A prose summary of what applying a proposal would do.
 *
 * Deliberately NOT a preview. A real preview would swap the player onto the
 * proposed edit while the timeline still showed the live one — two views
 * disagreeing about what the project is, which reads as a bug. Swapping both is
 * a much larger job (the timeline strip would have to suppress every edit
 * interaction or you can drag a phantom clip), and is worth doing properly
 * later rather than half-way now.
 *
 * So this describes the change instead of pretending to show it. It compares
 * the CURRENT edit with the replayed one, which means it also silently proves
 * the proposal still applies — if replay conflicts, the caller has a conflict
 * rather than a summary.
 */
import { clipEnd, totalDuration } from '../../format/helpers';
import type { AnyClip, Edit, Track, VideoClip } from '../../format/types';

export interface ProposalSummary {
  /** One line per observable change, in the order a person would notice them. */
  lines: string[];
  /** Before/after project duration, seconds. */
  durationBefore: number;
  durationAfter: number;
}

interface Counts {
  clipsByTrack: Map<string, number>;
  trackCount: number;
  clipIds: Set<string>;
  transitions: number;
  animated: number;
  totalClips: number;
}

function survey(edit: Edit): Counts {
  const clipsByTrack = new Map<string, number>();
  const clipIds = new Set<string>();
  let transitions = 0;
  let animated = 0;
  let totalClips = 0;

  for (const track of edit.tracks) {
    const clips = track.clips as AnyClip[];
    clipsByTrack.set(track.label, clips.length);
    totalClips += clips.length;
    for (const clip of clips) {
      clipIds.add(clip.id);
      const video = clip as VideoClip;
      if (video.transitionIn) transitions += 1;
      if (video.transform) {
        for (const value of Object.values(video.transform)) {
          if (value !== null && typeof value === 'object' && 'keyframes' in value) animated += 1;
        }
      }
    }
  }
  return {
    clipsByTrack,
    trackCount: edit.tracks.length,
    clipIds,
    transitions,
    animated,
    totalClips,
  };
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

function fmt(seconds: number): string {
  const s = Math.max(0, seconds);
  const m = Math.floor(s / 60);
  const rest = s - m * 60;
  return m > 0 ? `${m}m ${rest.toFixed(1)}s` : `${rest.toFixed(1)}s`;
}

/** Tracks that gained or lost clips, named individually rather than totalled. */
function trackDeltas(before: Counts, after: Counts): string[] {
  const lines: string[] = [];
  const labels = new Set([...before.clipsByTrack.keys(), ...after.clipsByTrack.keys()]);
  for (const label of [...labels].sort()) {
    const from = before.clipsByTrack.get(label) ?? 0;
    const to = after.clipsByTrack.get(label) ?? 0;
    if (from === to) continue;
    if (!before.clipsByTrack.has(label)) {
      lines.push(`New track ${label} with ${plural(to, 'clip')}`);
    } else if (!after.clipsByTrack.has(label)) {
      lines.push(`Removes track ${label}`);
    } else if (to > from) {
      lines.push(`${label}: ${plural(to - from, 'clip')} added (${from} → ${to})`);
    } else {
      lines.push(`${label}: ${plural(from - to, 'clip')} removed (${from} → ${to})`);
    }
  }
  return lines;
}

export function summariseProposal(before: Edit, after: Edit): ProposalSummary {
  const b = survey(before);
  const a = survey(after);
  const lines: string[] = [];

  if (a.trackCount > b.trackCount) {
    lines.push(`Adds ${plural(a.trackCount - b.trackCount, 'track')}`);
  }
  lines.push(...trackDeltas(b, a));

  // Clips that survive but moved: the common "it reordered things" case, which
  // a bare count of clips per track would hide completely.
  let moved = 0;
  const beforeStarts = new Map<string, number>();
  for (const track of before.tracks) {
    for (const clip of track.clips as AnyClip[]) beforeStarts.set(clip.id, clip.start);
  }
  for (const track of after.tracks) {
    for (const clip of track.clips as AnyClip[]) {
      const was = beforeStarts.get(clip.id);
      if (was !== undefined && Math.abs(was - clip.start) > 0.05) moved += 1;
    }
  }
  if (moved) lines.push(`${plural(moved, 'existing clip')} moved`);

  if (a.transitions !== b.transitions) {
    const delta = a.transitions - b.transitions;
    lines.push(
      delta > 0
        ? `${plural(delta, 'transition')} added`
        : `${plural(-delta, 'transition')} removed`,
    );
  }
  if (a.animated !== b.animated) {
    const delta = a.animated - b.animated;
    lines.push(
      delta > 0
        ? `${plural(delta, 'parameter')} animated`
        : `${plural(-delta, 'animation')} removed`,
    );
  }

  const durationBefore = totalDuration(before);
  const durationAfter = totalDuration(after);
  if (Math.abs(durationAfter - durationBefore) > 0.05) {
    lines.push(
      `Project length ${fmt(durationBefore)} → ${fmt(durationAfter)}` +
        ` (${durationAfter > durationBefore ? '+' : '−'}${fmt(Math.abs(durationAfter - durationBefore))})`,
    );
  }

  // Something changed that none of the above describes — say so rather than
  // showing an empty box that reads as "this does nothing".
  if (!lines.length) {
    lines.push('Adjusts clip properties without changing the arrangement');
  }
  return { lines, durationBefore, durationAfter };
}

/** Where the first new clip lands, for "…starting at 0:42" style context. */
export function firstAddedClipStart(before: Edit, after: Edit): number | null {
  const known = new Set<string>();
  for (const track of before.tracks) {
    for (const clip of track.clips as AnyClip[]) known.add(clip.id);
  }
  let earliest: number | null = null;
  for (const track of after.tracks as Track[]) {
    for (const clip of track.clips as AnyClip[]) {
      if (known.has(clip.id)) continue;
      if (earliest === null || clip.start < earliest) earliest = clip.start;
    }
  }
  return earliest;
}

export { clipEnd };
